import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Clock } from "lucide-react";
import { Container } from "../layout/Container";
import { cPath } from "../../constants";
import { getAllBlogPosts } from "../../services/blogServices";

const Section = styled.section`
  padding: 2rem 0 3rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled(Link)`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadow.card};
  }
`;

const ImgWrap = styled.div`
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: hsl(${({ theme }) => theme.colors.muted});
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s;
  }
  ${Card}:hover & img {
    transform: scale(1.04);
  }
`;

const Body = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: clamp(0.8125rem, 0.78rem + 0.15vw, 0.9375rem);
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  margin-bottom: 0.5rem;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem;
  font-size: clamp(1.0625rem, 0.95rem + 0.45vw, 1.25rem);
  font-weight: 700;
  color: hsl(${({ theme }) => theme.colors.foreground});
  line-height: 1.35;
  ${Card}:hover & {
    color: hsl(${({ theme }) => theme.colors.primary});
  }
`;

const Excerpt = styled.p`
  margin: 0;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  line-height: 1.5;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  flex: 1;
`;

const ReadMore = styled.span`
  margin-top: 0.75rem;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  font-weight: 600;
  color: hsl(${({ theme }) => theme.colors.primary});
`;

const Note = styled.p`
  text-align: center;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  padding: 2rem 1rem;
`;

export default function BlogListing() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const list = await getAllBlogPosts();
        if (!cancel) setPosts(list);
      } catch (e) {
        if (!cancel) {
          setError(e?.message || "Could not load articles");
          setPosts([]);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  if (loading) {
    return (
      <Section>
        <Container>
          <Note>Loading articles…</Note>
        </Container>
      </Section>
    );
  }

  if (error) {
    return (
      <Section>
        <Container>
          <Note>{error}</Note>
        </Container>
      </Section>
    );
  }

  if (!posts.length) {
    return (
      <Section>
        <Container>
          <Note>No articles published yet. Check back soon.</Note>
        </Container>
      </Section>
    );
  }

  return (
    <Section>
      <Container>
        <Grid>
          {posts.map((post) => (
            <Card key={post.slug} to={cPath(`blog/${post.slug}`)}>
              {post.image ? (
                <ImgWrap>
                  <img src={post.image} alt="" loading="lazy" />
                </ImgWrap>
              ) : null}
              <Body>
                <Meta>
                  <span>{post.date}</span>
                  <span>·</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Clock size={12} /> {post.readMins} min read
                  </span>
                </Meta>
                <Title>{post.title}</Title>
                <Excerpt>{post.excerpt}</Excerpt>
                <ReadMore>Read article →</ReadMore>
              </Body>
            </Card>
          ))}
        </Grid>
      </Container>
    </Section>
  );
}
